import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, imageUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { usePageContent } from "@/hooks/usePageContent";
import WatchCard from "@/components/WatchCard";
import { ArrowRight } from "lucide-react";

const HomePage = () => {
  const { settings } = useSettings();
  const c = usePageContent("home");
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);

  useEffect(() => {
    http.get("/watches", { params: { featured: true, limit: 6 } }).then((r) => setFeatured(r.data));
    http.get("/watches", { params: { new_arrival: true, limit: 6 } }).then((r) => setNewArrivals(r.data));
  }, []);

  const heroImg = imageUrl(c.hero_image) || settings?.hero_image ||
    "https://images.pexels.com/photos/1137061/pexels-photo-1137061.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1900";

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[560px] overflow-hidden bg-[var(--aq-cream)]">
        <img
          src={heroImg}
          alt="AQ Watches"
          className="aq-zoom absolute inset-0 w-full h-full object-cover opacity-95"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/25 to-black/55" />
        <div className="relative h-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 flex items-end pb-20 lg:pb-28">
          <div className="aq-reveal max-w-2xl">
            <p className="text-white/85 text-[11px] uppercase tracking-[0.32em] mb-6">{c.hero_eyebrow || "AQ Watches · Atelier de Relojería Personalizada"}</p>
            <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
              {settings?.hero_headline || "Relojes Personalizados y Piezas a la Medida"}
            </h1>
            <p className="text-white/90 text-base sm:text-lg max-w-xl leading-relaxed mb-10">
              {settings?.hero_subheadline || "Relojes ensamblados a mano, diseñados alrededor de tu estilo."}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/catalog" data-testid="hero-explore-cta" className="btn-primary bg-white text-black hover:bg-white/90">
                Explorar el Catálogo <ArrowRight size={16} />
              </Link>
              <Link to="/configurator" data-testid="hero-custom-cta" className="btn-outline border-white text-white hover:bg-white hover:text-black">
                Crear el Tuyo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[var(--aq-border)] py-5 overflow-hidden bg-white">
        <div className="aq-marquee text-[var(--aq-muted)] text-[11px] uppercase tracking-[0.32em]">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-16 pr-16">
              <span>Ensamblado a Mano</span><span>·</span>
              <span>Piezas Personalizadas</span><span>·</span>
              <span>Cristal de Zafiro</span><span>·</span>
              <span>Bisel Personalizado</span><span>·</span>
              <span>Diseños Únicos</span><span>·</span>
              <span>Consultas por WhatsApp</span><span>·</span>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-6 sm:px-10 lg:px-14">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <p className="overline mb-4">{c.featured_overline || "El Atelier"}</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl">{c.featured_title || "Modelos Destacados"}</h2>
          </div>
          <Link to="/catalog" className="text-[11px] uppercase tracking-[0.28em] border-b border-black pb-1 hover:opacity-70">
            Ver todo el catálogo
          </Link>
        </div>

        {featured.length === 0 ? (
          <p className="text-[var(--aq-muted)] text-sm">Aún no hay piezas destacadas.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
            {featured.map((w) => <WatchCard key={w.id} watch={w} />)}
          </div>
        )}
      </section>

      {/* SPLIT — about / craftsmanship */}
      <section className="bg-[var(--aq-cream)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          <div>
            <p className="overline mb-4">{c.craft_overline || "Nuestra Artesanía"}</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-6 leading-[1.1]">
              {c.craft_title || "Donde la paciencia se encuentra con la precisión."}
            </h2>
            <p className="text-[var(--aq-muted)] leading-relaxed mb-4">
              {c.craft_text_1 || "Cada pieza de AQ Watches comienza con una conversación, un boceto y un brief."}
            </p>
            <p className="text-[var(--aq-muted)] leading-relaxed mb-8">
              {c.craft_text_2 || "No hay dos iguales. Cada reloj se construye para ser el único de su tipo en tu muñeca."}
            </p>
            <Link to="/configurator" className="btn-outline">{c.craft_cta || "Solicitar un Diseño"}</Link>
          </div>
          <div className="aspect-[4/5] overflow-hidden">
            <img
              src={imageUrl(c.craft_image) || "https://images.pexels.com/photos/4276458/pexels-photo-4276458.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"}
              alt="artesanía" className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-6 sm:px-10 lg:px-14">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <p className="overline mb-4">{c.new_overline || "Reciente"}</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl">{c.new_title || "Nuevos Modelos"}</h2>
          </div>
          <Link to="/catalog" className="text-[11px] uppercase tracking-[0.28em] border-b border-black pb-1 hover:opacity-70">
            Ver catálogo
          </Link>
        </div>

        {newArrivals.length === 0 ? (
          <p className="text-[var(--aq-muted)] text-sm">Aún no hay nuevos ingresos.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
            {newArrivals.map((w) => <WatchCard key={w.id} watch={w} />)}
          </div>
        )}
      </section>
    </div>
  );
};

export default HomePage;
